import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader } from "../components/common";
import { toast } from "sonner";

const GROUPS = [
  { key: "leads", label: "Leads", tone: "bg-purple-50 text-purple-700", to: (r) => `/app/crm/contas/${r.id}` },
  { key: "contas", label: "Contas", tone: "bg-blue-50 text-blue-700", to: (r) => `/app/crm/contas/${r.id}` },
  { key: "contactos", label: "Contactos", tone: "bg-teal-50 text-teal-700",
    to: (r) => r.account_id ? `/app/crm/contas/${r.account_id}` : `/app/crm/contactos` },
  { key: "subscricoes", label: "Subscricoes", tone: "bg-green-50 text-green-700", to: () => `/app/subscricoes` },
  { key: "tickets", label: "Tickets", tone: "bg-amber-50 text-amber-700", to: () => `/app/suporte` },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [q, setQ] = useState(params.get("q") || "");
  const [plataforma, setPlataforma] = useState(params.get("plataforma") || "");
  const [tipo, setTipo] = useState(params.get("tipo") || "");
  const [platformOptions, setPlatformOptions] = useState([]);
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/platforms/lookup").then(({ data }) =>
      setPlatformOptions((data || []).map((p) => ({ value: p.nome, label: p.nome })))
    ).catch(() => {});
  }, []);

  useEffect(() => {
    const term = (params.get("q") || "").trim();
    const plat = params.get("plataforma") || "";
    setQ(params.get("q") || "");
    setPlataforma(plat);
    setTipo(params.get("tipo") || "");
    if (!term) { setGroups({}); return; }
    setLoading(true);
    api.get("/global-search", { params: { q: term, plataforma: plat || undefined, limit: 20 } })
      .then(({ data }) => setGroups(data || {}))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [params]);

  const submit = (e) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (q.trim()) next.set("q", q.trim());
    if (plataforma) next.set("plataforma", plataforma);
    if (tipo) next.set("tipo", tipo);
    setParams(next);
  };

  const selectTipo = (t) => {
    const next = new URLSearchParams(params);
    if (t) next.set("tipo", t);
    else next.delete("tipo");
    setParams(next);
  };

  const total = GROUPS.reduce((n, g) => n + (groups[g.key]?.length || 0), 0);
  const visibleGroups = tipo ? GROUPS.filter((g) => g.key === tipo) : GROUPS;

  return (
    <div className="max-w-6xl mx-auto pb-10">
      <PageHeader kicker="Pesquisa" title="Resultados da pesquisa" />
      <form onSubmit={submit} className="flex gap-2 mb-6" data-testid="search-page-form">
        <select
          value={plataforma}
          onChange={(e) => setPlataforma(e.target.value)}
          data-testid="search-page-platform"
          className="text-sm bg-white border border-slate-300 rounded-md px-3 focus:ring-2 focus:ring-blue/20 focus:outline-none"
        >
          <option value="">Todas plataformas</option>
          {platformOptions.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <div className="relative flex-1">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="search-page-input"
            placeholder="Pesquisar leads, contas, contactos, subscricoes, tickets..."
            className="w-full pl-8 pr-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-blue/20 focus:outline-none"
          />
        </div>
        <button type="submit" data-testid="search-page-submit"
          className="text-sm px-4 py-2 rounded-md bg-blue text-white font-semibold hover:bg-navy">
          Pesquisar
        </button>
      </form>

      {loading && <div className="text-sm text-muted2">A pesquisar...</div>}
      {!loading && !q.trim() && (
        <div className="text-sm text-muted2 bg-tint rounded-lg p-6 text-center">
          Escreva um termo acima para pesquisar em leads, contas, contactos, subscricoes e tickets.
        </div>
      )}
      {!loading && q.trim() && (
        <div className="mb-5 flex items-center gap-2 flex-wrap" data-testid="search-page-tipo-chips">
          <button onClick={() => selectTipo("")}
            data-testid="search-page-tipo-all"
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${!tipo ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-200 hover:border-navy"}`}>
            Todos <span className="opacity-70">({total})</span>
          </button>
          {GROUPS.map((g) => {
            const n = (groups[g.key] || []).length;
            const active = tipo === g.key;
            return (
              <button key={g.key} onClick={() => selectTipo(g.key)}
                data-testid={`search-page-tipo-${g.key}`}
                disabled={n === 0}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${active ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-200 hover:border-navy"}`}>
                {g.label} <span className="opacity-70">({n})</span>
              </button>
            );
          })}
        </div>
      )}
      {!loading && q.trim() && total === 0 && (
        <div className="text-sm text-muted2 bg-tint rounded-lg p-6 text-center">
          Sem resultados para "<b className="text-navy">{q}</b>"
          {plataforma && <> na plataforma <b className="text-navy">{plataforma}</b></>}.
        </div>
      )}

      <div className="space-y-6">
        {visibleGroups.map(({ key, label, tone, to }) => {
          const rows = groups[key] || [];
          if (!rows.length) return null;
          return (
            <section key={key} data-testid={`search-page-group-${key}`}>
              <h3 className="text-navy text-sm font-bold uppercase tracking-wider mb-2">
                {label} <span className="text-muted2">({rows.length})</span>
              </h3>
              <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
                {rows.map((r) => (
                  <button key={r.id} onClick={() => nav(to(r))}
                    data-testid={`search-page-item-${key}-${r.id}`}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 text-sm flex items-center gap-3">
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${tone}`}>{label.slice(0, -1) || label}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-navy truncate">
                        {r.nome || r.assunto || r.organizacao_nome || r.plano_nome || "-"}
                      </div>
                      <div className="text-xs text-muted2 truncate">
                        {key === "leads" && `${r.origem ? "origem " + r.origem : ""}${r.nif ? " - NIF " + r.nif : ""}`}
                        {key === "contas" && `${r.nif ? "NIF " + r.nif : ""}${r.tipo ? " - " + r.tipo : ""}`}
                        {key === "contactos" && `${r.email || ""}${r.organizacao_nome ? " - " + r.organizacao_nome : ""}`}
                        {key === "subscricoes" && `${r.organizacao_nome || ""}${r.plano_nome ? " - " + r.plano_nome : ""}${r.mrr ? " - " + r.mrr + " EUR/mes" : ""}`}
                        {key === "tickets" && `${r.numero || ""}${r.organizacao_nome ? " - " + r.organizacao_nome : ""}`}
                      </div>
                    </div>
                    {r.plataforma && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{r.plataforma}</span>}
                    {r.estado && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{r.estado}</span>}
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
